import React from 'react';
import '@inovua/reactdatagrid-community/index.css';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab, {tabClasses} from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import {UserGeneralTable} from "./usersTables/UserGeneralTable";
import {UserStatisticTable} from "./usersTables/UserStatisticTable";
import Button from "@mui/joy/Button";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Divider from "@mui/joy/Divider";
import Sheet from "@mui/joy/Sheet";
import Input from "@mui/joy/Input";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import {NotificationService} from "../../common/notification";
import {AdminPenaltySummary, createFakeUserApi, penaltySummaryApi, unbanAllPenaltiesApi} from "./usersApi";
import ConfirmDialog from "../../components/confirmDialog/ConfirmDialog";

export function Users() {
    const [openCreate, setOpenCreate] = React.useState(false);
    const [steamId, setSteamId] = React.useState<string>("");
    const [submitting, setSubmitting] = React.useState(false);
    const [openUnbanAllConfirm, setOpenUnbanAllConfirm] = React.useState(false);
    const [unbanAllSubmitting, setUnbanAllSubmitting] = React.useState(false);
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [openPenaltySummary, setOpenPenaltySummary] = React.useState(false);
    const [penaltySummary, setPenaltySummary] = React.useState<AdminPenaltySummary | null>(null);
    const [penaltySummaryLoading, setPenaltySummaryLoading] = React.useState(false);
    const isProd = process.env.NODE_ENV === 'production';

    const onCreate = async () => {
        if (!steamId || !/^\d{17}$/.test(steamId)) {
            NotificationService.alertError("Введите корректный Steam ID (17 цифр)");
            return;
        }
        setSubmitting(true);
        try {
            const result = await createFakeUserApi(BigInt(steamId));
            if (result.result) {
                NotificationService.alertSuccess("Пользователь создан (фейк)");
                setOpenCreate(false);
                setSteamId("");
            } else {
                NotificationService.alertError("Не удалось создать пользователя. Попробуйте позже");
            }
        } catch (e) {
            NotificationService.alertError("Ошибка при создании пользователя");
        } finally {
            setSubmitting(false);
        }
    };

    const onUnbanAllConfirm = async () => {
        setUnbanAllSubmitting(true);
        try {
            const result = await unbanAllPenaltiesApi();
            if (result.result && result.value) {
                const {unbannedCount: count, failedCount} = result.value;
                if (failedCount > 0) {
                    NotificationService.alertError(
                        `Штрафы и баны сняты у ${count} пользователей, у ${failedCount} — ошибка (подробности в Slack)`
                    );
                } else {
                    NotificationService.alertSuccess(
                        count > 0
                            ? `Штрафы и баны сняты у ${count} пользователей`
                            : "Нет пользователей со штрафными очками или баном"
                    );
                }
                setRefreshKey((k) => k + 1);
            } else {
                NotificationService.alertError("Не удалось снять штрафы. Попробуйте позже");
            }
        } catch (e) {
            NotificationService.alertError("Ошибка при снятии штрафов");
        } finally {
            setUnbanAllSubmitting(false);
        }
    };

    const onOpenPenaltySummary = async () => {
        setPenaltySummaryLoading(true);
        try {
            const result = await penaltySummaryApi();
            if (result.result && result.value) {
                setPenaltySummary(result.value);
                setOpenPenaltySummary(true);
            } else {
                NotificationService.alertError("Не удалось получить информацию о штрафах. Попробуйте позже");
            }
        } catch (e) {
            NotificationService.alertError("Ошибка при получении информации о штрафах");
        } finally {
            setPenaltySummaryLoading(false);
        }
    };

    return (
        <React.Fragment>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: {
                        xs: 'calc(12px + var(--Header-height))',
                        sm: 'calc(12px + var(--Header-height))',
                        md: 3,
                    },
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    height: '100dvh',
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'start', sm: 'center' },
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        Пользователи
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            color="neutral"
                            variant="outlined"
                            onClick={onOpenPenaltySummary}
                            loading={penaltySummaryLoading}
                            disabled={penaltySummaryLoading}
                        >
                            Информация о штрафах
                        </Button>
                        <Button
                            color="neutral"
                            variant="outlined"
                            onClick={() => setOpenUnbanAllConfirm(true)}
                            loading={unbanAllSubmitting}
                            disabled={unbanAllSubmitting}
                        >
                            Снять штрафы всем
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                if (isProd) {
                                    NotificationService.alertError("Создание недоступно в PROD среде");
                                    return;
                                }
                                setOpenCreate(true);
                            }}
                        >
                            Создать
                        </Button>
                    </Box>
                </Box>
                <ConfirmDialog
                    title="Снять штрафы всем"
                    open={openUnbanAllConfirm}
                    setOpen={setOpenUnbanAllConfirm}
                    onConfirm={onUnbanAllConfirm}
                >
                    Вы уверены, что хотите снять штрафные очки и баны у всех пользователей с активными штрафами?
                </ConfirmDialog>
                <Modal open={openPenaltySummary} onClose={() => setOpenPenaltySummary(false)}>
                    <ModalDialog>
                        <ModalClose />
                        <Typography level="h2">Информация о штрафах</Typography>
                        <Divider sx={{ my: 2 }} />
                        <Sheet sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography>
                                В бане на текущий момент: <b>{penaltySummary?.bannedUsersCount ?? 0}</b>
                            </Typography>
                            <Typography>
                                Со штрафами без бана: <b>{penaltySummary?.finedWithoutBanUsersCount ?? 0}</b>
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Button color="neutral" onClick={() => setOpenPenaltySummary(false)}>
                                    Закрыть
                                </Button>
                            </Box>
                        </Sheet>
                    </ModalDialog>
                </Modal>
                <Modal open={openCreate} onClose={() => setOpenCreate(false)}>
                    <ModalDialog>
                        <ModalClose />
                        <Typography level="h2">Создание фейкового пользователя</Typography>
                        <Divider sx={{ my: 2 }} />
                        <Sheet sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography level="body-sm">
                                Создание фейкового пользователя. Предназначено для тестирования инвентаря. В PROD среде создание невозможно.
                            </Typography>
                            <FormControl>
                                <FormLabel>SteamId</FormLabel>
                                <Input
                                    placeholder="Введите Steam ID (17 цифр)"
                                    value={steamId}
                                    onChange={(e) => setSteamId(e.target.value)}
                                />
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button color="neutral" onClick={() => setOpenCreate(false)}>
                                    Отмена
                                </Button>
                                <Button color="primary" loading={submitting} onClick={onCreate}>
                                    Создать
                                </Button>
                            </Box>
                        </Sheet>
                    </ModalDialog>
                </Modal>
                <Tabs defaultValue={0} sx={{bgcolor: 'transparent'}}>
                    <TabList
                        tabFlex={1}
                        size="sm"
                        sx={{
                            pl: {xs: 0, md: 4},
                            justifyContent: 'left',
                            [`&& .${tabClasses.root}`]: {
                                fontWeight: '600',
                                flex: 'initial',
                                color: 'text.tertiary',
                                [`&.${tabClasses.selected}`]: {
                                    bgcolor: 'transparent',
                                    color: 'text.primary',
                                    '&::after': {
                                        height: '2px',
                                        bgcolor: 'primary.500',
                                    },
                                },
                            },
                        }}
                    >
                        <Tab sx={{borderRadius: '6px 6px 0 0'}} indicatorInset value={0}>
                            Общая таблица
                        </Tab>
                        <Tab sx={{borderRadius: '6px 6px 0 0'}} indicatorInset value={1}>
                            Таблица статистики
                        </Tab>
                    </TabList>
                    <TabPanel value={0}>
                        <UserGeneralTable key={refreshKey}/>
                    </TabPanel>
                    <TabPanel value={1}>
                        <UserStatisticTable/>
                    </TabPanel>
                </Tabs>
            </Box>

        </React.Fragment>
    );
}
